import { RefObject, useEffect, useRef, useState } from 'react';

// Dots for a CSS scroll-snap row (one snap position per child). count = positions that can reach the left edge
// (children - children per view + 1); 1 when the row doesn't scroll (e.g. the desktop grid), so callers hide the dots.
// Uses offsetWidth (layout px) to match scrollLeft / clientWidth — getBoundingClientRect is in zoomed px (mobile / Text size zoom).
export const useCarousel = <T extends HTMLElement>(deps: any[] = []): { ref: RefObject<T>; index: number; count: number; onScroll: () => void; goTo: (i: number) => void } => {
    const ref = useRef<T>(null);
    const [slides, setSlides] = useState({ index: 0, count: 1 });

    const step = () => {
        const el = ref.current;
        const first = el?.firstElementChild as HTMLElement | null;
        if (!el || !first) return 0;
        return first.offsetWidth + parseFloat(getComputedStyle(el).columnGap || '0');
    };

    const onScroll = () => {
        const el = ref.current;
        const s = step();
        if (!el || !s || el.scrollWidth <= el.clientWidth + 1) {
            setSlides({ index: 0, count: 1 });
            return;
        }
        const perView = Math.max(1, Math.round((el.clientWidth + parseFloat(getComputedStyle(el).columnGap || '0')) / s));
        const count = el.children.length - perView + 1;
        setSlides({ index: Math.min(count - 1, Math.round(el.scrollLeft / s)), count });
    };

    const goTo = (i: number) => ref.current?.scrollTo({ left: i * step(), behavior: 'smooth' });

    useEffect(() => {
        onScroll();
        window.addEventListener('resize', onScroll);
        return () => window.removeEventListener('resize', onScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { ref, index: slides.index, count: slides.count, onScroll, goTo };
};

export default useCarousel;
